import { PublisherSlotAPI } from '@mbrtargeting/metatag-publisher-api';
import { LogEntry } from './infotool-api.js';

/**
 * All system events use the "window" object as the "eventTarget".
 */
export enum SystemEvent {
    /**
     * @todo implement or remove
     */
    SDG_ADP_MODULE_LOADED = 'metaTagSystemAdpModuleLoaded',
    /**
     * used as workaround in PhaseController, to not link directly into consent-cache module
     * @internal MetaTag
     */
    SDG_CMP_CACHED_CONSENT_AVAILABLE = 'metaTagSystemCmpCachedConsentAvailable',
    /**
     * This event signals that the user is being shown a message layer from the consent manager.
     * This layer covers most of the elements from the webpage.
     * It is encouraged that certain load processes from the website, which are not initially essential, only load once the user can see/interact with them.
     * In the case that a vendor relies on legitimate interest (some vendors are essential for the use of a website), this vendor will be classified as 'allowed' at this point.
     * Keep in mind that the user has the possibility to decline his/her consent for legitimate interest.
     * Once the message gets accepted or closes, the event: metaTagSystemCmpChoiceSelected gets fired.
     *
     * @see {@link https://stroeerdigitalgroup.atlassian.net/l/cp/rouQ3Gyq} publisher documentation
     */
    SDG_CMP_CHOICE_UI_SHOWN = 'metaTagSystemCmpChoiceUiShown',
    /**
     * Gets triggered once the user has finished interacting with the communication layer of the consent manager platform.
     * At this point not all the necessary information will be available.
     * In order to listen for the complete available consent information please use the event: metaTagSystemCmpConsentAvailable
     *
     * Attention: A deprecated version of MetaTag used the event: "metaTagSystemChoiceSelected". This event as the now updated event: metaTagSystemCmpConsentAvailable are still both available
     *
     * @see {@link https://stroeerdigitalgroup.atlassian.net/l/cp/rouQ3Gyq} publisher documentation
     */
    SDG_CMP_CHOICE_SELECTED = 'metaTagSystemCmpChoiceSelected', // newer variant, migrate users to this if possible
    SDG_CHOICE_SELECTED = 'metaTagSystemChoiceSelected', // old variant, still supported and used
    /**
     * Gets triggered once the user has set his/her consent for all vendors (IAB/Non IAB).
     * At this point in the systems process you are able to get the consent information pro vendor from our publisher interface methods:
     * SDG.Publisher.getIABVendorConsent() or SDG.Publisher.getCustomVendorConsent().
     *
     * This event only gets fired once per page impression even if a user changes his consent while on the page.
     * (for example by opening the privacy manager)
     *
     * @see {@link https://stroeerdigitalgroup.atlassian.net/l/cp/rouQ3Gyq} publisher documentation
     */
    SDG_CMP_FULL_CONSENT_AVAILABLE = 'metaTagSystemCmpConsentAvailable',
    /**
     * Gets triggered once the event DOMContentLoaded has fired.
     *
     * @see {@link https://stroeerdigitalgroup.atlassian.net/l/cp/rouQ3Gyq} publisher documentation
     */
    SDG_DOM_CONTENT_LOADED = 'metaTagSystemContentLoaded',
    /**
     * Gets triggered before the first call from an ad slot is send to the ad server.
     *
     * @see {@link https://stroeerdigitalgroup.atlassian.net/l/cp/rouQ3Gyq} publisher documentation
     */
    SDG_BEFORE_LOAD_ALL = 'metaTagSystemBeforeLoadAll',
    /**
     * TODO describe event
     *
     * @internal InfoTool
     */
    SDG_NEW_LOG_ENTRY = 'metaTagSystemNewLogEntry',
    /**
     * Gets triggered once MetaTag has decided which ad server to use on the website.
     * Legacy, used by some websites to indicate "metatag commands can now be sent".
     * todo Migrate publishers to command queue and remove "SDG_AD_SERVER_MODULE_LOADED" event
     * @see {@link https://stroeerdigitalgroup.atlassian.net/l/cp/rouQ3Gyq} publisher documentation
     */
    SDG_AD_SERVER_MODULE_LOADED = 'metaTagSystemAdServerModuleLoaded',
    /**
     * Gets triggered when an Element has been found on the page which measurements coincide with the width and height of the content.
     * Some formats do not start loading until this event has been fired.
     *
     * @see {@link https://stroeerdigitalgroup.atlassian.net/l/cp/rouQ3Gyq} publisher documentation
     */
    SDG_CONTENT_ELEMENT_LOADED = 'metaTagSystemContentElementLoaded',
    /**
     * @todo implement or remove, keep until MetaTag2 is completely rolled out
     */
    SDG_CRITICAL_MODULES_LOADED = 'metaTagSystemCriticalModulesLoaded',
    /**
     * @todo implement or remove, keep until MetaTag2 is completely rolled out
     */
    SDG_PREBID_RESOURCE_LOADED = 'metaTagSystemPrebidResourceLoaded',
    /**
     * Gets triggered once all real time auctions have finished from the open source solution "Prebid".
     * Prebid is not active on all websites.
     *
     * @see {@link https://stroeerdigitalgroup.atlassian.net/l/cp/rouQ3Gyq} publisher documentation
     * @todo implement or remove
     */
    SDG_PREBID_RESPONDED = 'metaTagSystemPrebidResponded',
    /**
     * Triggered by Yieldlove Prebid as soon as the full auction has run its course.
     * Used in MetaTag1 to wait for the auction to end, and send all participating ad slots to next creative source
     * No direct use in MetaTag2, beside logging & performance.
     */
    SDG_YIELDLOVE_AUCTION_DONE = 'metaTagSystemYieldloveAuctionDone',
    /**
     * Gets triggered once our interface method setZone() has been completed.
     *
     * @see {@link https://stroeerdigitalgroup.atlassian.net/l/cp/rouQ3Gyq} publisher documentation
     */
    SDG_ZONE_SET = 'metaTagSystemZoneSet',
    /**
     * Gets triggered once our interface method setPageType() has been completed.
     *
     * @see {@link https://stroeerdigitalgroup.atlassian.net/l/cp/rouQ3Gyq} publisher documentation
     */
    SDG_PAGETYPE_SET = 'metaTagSystemPageTypeSet',
    /**
     * Is triggered when Publisher executes SDG.Publisher.finalizeSlots(). Indicates that Publisher did register all important slots (for now), and we can start the targeting/delivery process
     */
    SDG_SLOTS_FINALIZED = 'metaTagSystemSlotsFinalized',
    /**
     * Legacy from MetaTag1, used by many websites to base their ad integration on (since it signals MetaTag is fully loaded).
     * Formerly triggered when window was fully loaded, now fired as soon as core file is fully loaded.
     */
    SDG_WINDOW_LOAD_COMPLETE = 'metaTagSystemWindowLoadComplete',

    /**
     * Triggered when we ask a NetId user for consent to use his NetId and the permission was given.
     * The consent status was written to the NetId consent service without error and was eventually written to our local cache.
     *
     * @see {@link https://miro.com/app/board/o9J_krzpQwY=/?moveToWidget=3458764532488656543&cot=14 } miro board with NetId events
     */
    NETID_RESPONDED_POSITIVE = 'metaTagSystemNetIdRespondedPositive',
    /**
     * Triggered when we ask a NetId user for consent to use his NetId, but the permission was declined.
     * The consent status was written to the NetId consent service without error and was eventually written to our local cache.
     *
     * @see {@link https://miro.com/app/board/o9J_krzpQwY=/?moveToWidget=3458764532488656543&cot=14 } miro board with NetId events
     */
    NETID_RESPONDED_NEGATIVE = 'metaTagSystemNetIdRespondedNegative',
    /**
     * Triggered when the NetId service responds that the user has a NetId account and we are allowed to use the ID.
     *
     * @see {@link https://miro.com/app/board/o9J_krzpQwY=/?moveToWidget=3458764532488656543&cot=14 } miro board with NetId events
     */
    NETID_RESPONDED_USER_KNOWN = 'metaTagSystemNetIdRespondedUserKnown',
    /**
     * Triggered when the NetId service responds that the user has no NetId account.
     *
     * @see {@link https://miro.com/app/board/o9J_krzpQwY=/?moveToWidget=3458764532488656543&cot=14 } miro board with NetId events
     */
    NETID_RESPONDED_USER_UNKNOWN = 'metaTagSystemNetIdRespondedUserUnknown',
    /**
     * This event is triggered when we have a cached NetId status of the user which is still valid and not expired.
     *
     * @see {@link https://miro.com/app/board/o9J_krzpQwY=/?moveToWidget=3458764532488656543&cot=14 } miro board with NetId events
     */
    NETID_RESPONDED_USERVALID = 'metaTagSystemNetIdRespondedUserValid',
    /**
     * This event is triggered when user has a NetId account (this does not mean that we allowed to use it!)
     * It is triggered once per page impression when we ask the NetId service or use its cached state.
     *
     * @see {@link https://miro.com/app/board/o9J_krzpQwY=/?moveToWidget=3458764532488656543&cot=14 } miro board with NetId events
     */
    NETID_HAS_ACCOUNT = 'metaTagSystemNetIdHasNetIdAccount',
    /**
     * This event is triggered when user has no NetId account or this information is not available.
     * It is triggered once per page impression when we ask the NetId service or use its cached state.
     *
     * @see {@link https://miro.com/app/board/o9J_krzpQwY=/?moveToWidget=3458764532488656543&cot=14 } miro board with NetId events
     */
    NETID_HAS_NO_ACCOUNT = 'metaTagSystemNetIdHasNoNetIdAccount',
    /**
     * This error event is triggered when reading from or writing to the NetId service fails.
     *
     * @see {@link https://miro.com/app/board/o9J_krzpQwY=/?moveToWidget=3458764532488656543&cot=14 } miro board with NetId events
     */
    NETID_RESPONDED_ERROR = 'metaTagSystemNetIdRespondedError',
    /**
     * Triggered when the user has a NetId, but we need to ask for consent to use it.
     *
     * @see {@link https://miro.com/app/board/o9J_krzpQwY=/?moveToWidget=3458764532488656543&cot=14 } miro board with NetId events
     */
    NETID_SHOW_LAYER = 'metaTagSystemNetIdShowLayer',
    /**
     * Gets triggered when we call NetId service to check if user has a NetId account.
     *
     * @see {@link https://miro.com/app/board/o9J_krzpQwY=/?moveToWidget=3458764532488656543&cot=14 } miro board with NetId events
     */
    NETID_CHECK_USER = 'metaTagSystemNetIdCheckUser',
    /**
     * @todo implement or remove, keep until metaTag2 is rolled out completely
     */
    SDG_PERSISTANT_ID_AVAILABLE = 'metaTagSystemPersistantIdAvailable',
    /**
     * Triggered when primary file from MetaTag2 (config and basic functionality) is loaded and present on website.
     * Indicates that we can look into the config and we know what we have to further load.
     *
     * @internal MetaTag
     */
    SDG_CONFIG_FILE_AVAILABLE = 'metaTagSystemConfigFileAvailable',
    /**
     * Is triggered when the secondary file from MetaTag2 (main logics and code) are loaded and present on the website.
     * Indicates that we can proceed setting up MetaTag fully and all resources are present to do so.
     * @internal MetaTag
     */
    SDG_CORE_FILE_LOADED = 'metaTagCoreFileLoaded',
    /**
     * Will inform adslot queue state that delivery phase can proceed, after targeting phase has finished.
     * @internal MetaTag
     */
    DELIVERY_PHASE_STARTED = 'metaTagSystemDeliveryPhaseStarted',
    /**
     * Will inform when tacking phase has started, after tracking phase has finished.
     * @internal MetaTag
     */
    TRACKING_PHASE_STARTED = 'metaTagSystemTrackingPhaseStarted',
    /**
     * we already have a utiq id
     */
    UTIQ_ID_PRESENT = 'metaTagSystemUtiqIdPresent',
    /**
     * we do not have a utiq id yet
     */
    UTIQ_ID_ABSENT = 'metaTagSystemUtiqIdAbsent',
    /**
     * user it not eligible to be identified by utiq (has no telco connection)
     */
    UTIQ_USER_NOT_ELIGIBLE = 'metaTagSystemUtiqUserNotEligible',
    /**
     * user is eligible to potentially be identified by utiq (has telco connection)
     */
    UTIQ_USER_ELIGIBLE = 'metaTagSystemUtiqUserIsEligible',
    /**
     * user is eligible to be identified by utiq (has telco connection), but has opt-out for this domain
     */
    UTIQ_USER_ELIGIBLE_NOT_DOMAIN = 'metaTagSystemUtiqUserIsEligibleNotDomain',
    /**
     * user has given consent to use utiq
     */
    UTIQ_CONSENT_TRUE = 'metaTagSystemUtiqConsentTrue',
    /**
     * user has not given consent to use utiq yet
     */
    UTIQ_CONSENT_FALSE = 'metaTagSystemUtiqConsentFalse',
    /**
     * utiq consent layer was shown
     */
    UTIQ_LAYER_SHOW = 'metaTagSystemUtiqLayerShow',
    /**
     * user accepts consent via utiq consent layer
     */
    UTIQ_LAYER_ACCEPT = 'metaTagSystemUtiqLayerAccept',
    /**
     * user declines consent via utiq consent layer
     */
    UTIQ_LAYER_REJECT = 'metaTagSystemUtiqLayerReject',
}

/**
 * All system slot events use the "window" object as the "eventTarget".
 */
export enum SystemSlotEvent {
    /**
     * Gets triggered as soon as an ad slot is deleted or unregistered.
     * This event contains general information for the ad slots.
     *
     * @see {@link https://stroeerdigitalgroup.atlassian.net/l/cp/rouQ3Gyq} publisher documentation
     */
    SDG_SLOT_DELETED = 'metaTagSystemSlotDeleted',
    /**
     * Gets triggered as soon as an ad slot is registered.
     * This event contains general information for the ad slots.
     *
     * @see {@link https://stroeerdigitalgroup.atlassian.net/l/cp/rouQ3Gyq} publisher documentation
     */
    SDG_SLOT_REGISTERED = 'metaTagSystemSlotRegistered',
    /**
     * Gets triggered once an ad slot has received all information needed to start a call to the ad server.
     * This event contains general information for the ad slots.
     *
     * @see {@link https://stroeerdigitalgroup.atlassian.net/l/cp/rouQ3Gyq} publisher documentation
     * @todo maybe implement when use-case arises, when unused after metatag2 full rollout: remove
     */
    SDG_SLOT_PREPARED = 'metaTagSystemSlotPrepared',
    /**
     * Gets triggered once an ad slot starts a call to the ad server.
     * This event contains general information for the ad slots.
     *
     * @see {@link https://stroeerdigitalgroup.atlassian.net/l/cp/rouQ3Gyq} publisher documentation
     */
    SDG_SLOT_CALLING = 'metaTagSystemSlotCalling',
    /**
     * AdSlot has been rendered on the page, possibly already showing the creative.
     * Information about the creative is available via adslot-data-store
     */
    SDG_SLOT_RENDERED = 'metaTagSystemSlotRendered',
    /**
     * A creative will be written to the website, and we do know the dimensions of it.
     * Width x height dimensions are available via adslot-data-store.
     * Possibly used by some publishers to fuel their own CLS avoidance logic.
     */
    SDG_SLOT_SIZE_AVAILABLE = 'metaTagSystemSlotSizeAvailable',
    /**
     * internal signal to check for height changes based on delivered creative size, not to be used by publisher. Will be removed when switching to observables
     */
    SDG_SLOT_CREATIVE_AVAILABLE = 'metaTagSystemSlotCreativeAvailable',
    /**
     * internal signal to check for width changes based on delivered creative size, not to be used by publisher. Will be removed when switching to observables
     */
    SDG_SLOT_CREATIVE_WIDTH_AVAILABLE = 'metaTagSystemSlotCreativeWidthAvailable',
    /**
     * @todo implement or remove, see https://stroeerdigitalgroup.atlassian.net/browse/METATAG-418
     */
    SDG_SLOT_RELOAD = 'metaTagSystemSlotReload',
    /**
     * @todo implement or remove, see https://stroeerdigitalgroup.atlassian.net/browse/METATAG-418
     */
    SDG_SLOT_RELOAD_VISIBILITY = 'metaTagSystemSlotReloadVisibility',
    /**
     * Gets triggered once an ad slot has received an answer from the ad server and the slot has finished rendering an advertisement.
     * This event contains general information for the ad slots.
     *
     * @see {@link https://stroeerdigitalgroup.atlassian.net/l/cp/rouQ3Gyq} publisher documentation
     */
    SDG_SLOT_DONE = 'metaTagSystemSlotDone',
    /**
     * Gets triggered once an ad slot receives an answer from the ad server.
     * This event contains general information for the ad slots.
     *
     * @see {@link https://stroeerdigitalgroup.atlassian.net/l/cp/rouQ3Gyq} publisher documentation
     * @todo implement or remove
     */
    SDG_SLOT_RESPONDED = 'metaTagSystemSlotResponded',
    /**
     * Gets triggered once an ad slot receives an answer from the ad server that no advertisement is available.
     * This event will not necessarilly fire consistently.
     * It can depend on if the impression gets passed to another system from our original system.
     * The 3rd system might deliver an empty repsonse but not pass it on to us.
     *
     * @see {@link https://stroeerdigitalgroup.atlassian.net/l/cp/rouQ3Gyq} publisher documentation
     */
    SDG_SLOT_EMPTY = 'metaTagSystemSlotEmpty',
    /**
     * Triggered when MetaTag1 has found the Slot Container on website. Since MetaTag2 will create its own "SlotContainer" and attach it to the HTMLElement the Publisher has registered the slot onto, our SlotContainer is always available.
     * Might still by used by some templates and publisher code, therefor triggered in MetaTag2 as soon as Slot Lifecycle is "available".
     */
    SDG_SLOT_CONTAINER_AVAILABLE = 'metaTagSystemSlotContainerAvailable',
    /**
     * Triggered by Yieldlove Prebid as soon as the auction for the given adslots has run its course.
     * Used in MetaTag1 to wait for the auction to end, and send all participating ad slots to next creative source
     * No direct use in MetaTag2, beside logging & performance.
     */
    SDG_SLOT_AUCTION_COMPLETE = 'metaTagSystemSlotAuctionComplete',
    /**
     * Triggered by Yieldlove when ad slot will render a creative directly via PreBid.
     * No direct use in MetaTag2, beside logging & performance.
     */
    SDG_SLOT_DIRECT_RENDERING = 'metaTagSystemSlotDirectRendering',
    /**
     * Triggered by Yieldlove when a singular slot has run into an error during its auction.
     * Mostly because the ad slot is not present in Yieldlove config, and MetaTag config demands an auction for this slot.
     * No direct use in MetaTag2, beside logging & performance.
     */
    SDG_SLOT_AUCTION_ERROR = 'metaTagSystemSlotAuctionError',
    /**
     * Triggered by Yieldlove when a singular slot has run into an error during direct rendering the creative.
     * Possible cause is some malformed creative code.
     * No direct use in MetaTag2, beside logging & performance.
     */
    SDG_SLOT_YL_RENDER_ERROR = 'metaTagSystemSlotYlRenderError',
    /**
     * TODO describe event
     */
    SDG_SLOT_WILL_RELOAD = 'metaTagSystemSlotWillReload',
    /**
     * Triggered by Yieldlove, when PreBid will render an ad through the "normal" adserver chain (send keyvalues to adserver and wait for adserver to decide which creative to actually render).
     * No real use in MetaTag2. Could be removed as soon as Yieldlove is not triggering it anymore.
     */
    SDG_SLOT_AD_SERVER_RENDERING = 'metaTagSystemSlotAdServerRendering',
    /**
     * Triggered by Yieldlove when directly rendered ad has completed its rendering process without errors.
     * No real use in MetaTag2. Could be removed as soon as Yieldlove is not triggering it anymore.
     */
    SDG_SLOT_DIRECT_RENDERING_COMPLETE = 'metaTagSystemSlotDirectRenderComplete',
    /**
     * Triggered when lazy loaded adslot is near viewport of browser and should now start loading.
     * @internal MetaTag
     */
    SDG_SLOT_LAZY_LOAD_READY = 'metaTagSystemSlotLazyLoadReady',
}

/**
 * These events use the DOM node in which the ad slot was registered as the "eventTarget".
 *
 * Aside from the information available about the event itself, additionally you can access information about the ad slot.
 * For this use the first argument given through the listener method.
 * The data from the ad slot is available in "eventObject.detail".
 *
 * Since the ad slot starts firing multiple events after its registration it is advised to set your listeners before the ad slot gets registered.
 */
export enum SlotEvent {
    /**
     * The slot beeing listend on has been registerd and is available.
     *
     * @see {@link https://stroeerdigitalgroup.atlassian.net/l/cp/rouQ3Gyq} publisher documentation
     */
    SLOT_REGISTERED = 'metaTagSlotRegistered',
    /**
     * The slot beeing listend on has been unregistered/deleted and will not fire any more events.
     *
     * @see {@link https://stroeerdigitalgroup.atlassian.net/l/cp/rouQ3Gyq} publisher documentation
     */
    SLOT_DELETED = 'metaTagSlotDeleted',
    /**
     * @todo implement or remove
     */
    SLOT_CALLING = 'metaTagSlotCalling',
    /**
     * The slot being listened on has received and answer from the ad server, slot has not been rendered yet.
     *
     * @see {@link https://stroeerdigitalgroup.atlassian.net/l/cp/rouQ3Gyq} publisher documentation
     * @todo implement or remove
     */
    SLOT_RESPONDED = 'metaTagSlotResponded',
    /**
     * Gets triggered once an ad slot starts a call to the ad server.
     * This event contains general information for the ad slots.
     */
    SLOT_RENDERED = 'metaTagSlotRendered',
    /**
     * The slot being listend on has completed its process.
     * It has received an answer from the ad server and the slot has finished rendering an advertisement.
     *
     * @see {@link https://stroeerdigitalgroup.atlassian.net/l/cp/rouQ3Gyq} publisher documentation
     */
    SLOT_DONE = 'metaTagSlotDone',
    /**
     * The slot being listened on did not receive an advertisement from the ad server.
     * The slot removes itself from the webpage and does not reserve any more space on the page.
     * This event will not necessarily fire consistently.
     * It can depend on if the impression gets passed to another system from our original system.
     * The 3rd system might deliver an empty response but not pass it on to us.
     *
     * @see {@link https://stroeerdigitalgroup.atlassian.net/l/cp/rouQ3Gyq} publisher documentation
     */
    SLOT_EMPTY = 'metaTagSlotEmpty',
    /**
     * AdSlot has received a creative from an adserver, and does know its dimensions (width & height).
     * Can be used to react to height changes, for example changing the layout of the website to make more room for a larger creative.
     */
    SLOT_SIZE_AVAILABLE = 'metaTagSlotSizeAvailable',
}

export enum NodeEvent {
    /**
     * @todo implement or remove, see https://stroeerdigitalgroup.atlassian.net/browse/METATAG-418
     */
    SDG_NODE_NOW_STICKY = 'metaTagNodeNowSticky',
    /**
     * @todo implement or remove, see https://stroeerdigitalgroup.atlassian.net/browse/METATAG-418
     */
    SDG_NODE_NOW_INERT_AGAIN = 'metaTagNodeNowInertAgain',
}


/**
 * The default payload of all `SystemEvent` events.
 */
export interface SystemEventDetail {
}

/**
 * The default payload of all `SystemSlotEvent` events.
 */
export interface SystemSlotEventDetail extends SlotEventDetail {
}

/**
 * The default payload of all `SlotEvent` events.
 */
export interface SlotEventDetail {
    /** @deprecated use slotName instead */
    passedObject?: PublisherSlotAPI;
    /** @deprecated use slotName instead */
    placement?: PublisherSlotAPI;
    /** @deprecated use slotName instead */
    slot?: string;
    /** @deprecated use slotName instead */
    position?: string;
    slotName: string;
}

/**
 * The default payload of all `NodeEvent` events.
 */
export interface NodeEventDetail {
    passedObject: HTMLElement;
}

/**
 * Maps all `SystemEvent` events to their corresponding payload.
 */
export interface SystemEventDetailMap extends Record<SystemEvent, SystemEventDetail> {
    [SystemEvent.SDG_ZONE_SET]: { zone: string; };
    [SystemEvent.SDG_PAGETYPE_SET]: { pageType: string; };
    [SystemEvent.SDG_NEW_LOG_ENTRY]: { logEntry: LogEntry; };
}

/**
 * Maps all `SystemSlotEvent` events to their corresponding payload.
 */
export interface SystemSlotEventDetailMap extends Record<SystemSlotEvent, SystemSlotEventDetail> {
}

/**
 * Maps all `SlotEvent` events to their corresponding payload.
 */
export interface SlotEventDetailMap extends Record<SlotEvent, SlotEventDetail> {
}

/**
 * Maps all `NodeEvent` events to their corresponding payload.
 */
export interface NodeEventDetailMap extends Record<NodeEvent, NodeEventDetail> {
}

/**
 * A shortcut for all events sent to the `window` object.
 */
export type WindowEventDetailMap = SystemEventDetailMap & SystemSlotEventDetailMap;

/**
 * A shortcut for all events sent to an `HtmlElement`.
 */
export type HtmlElementEventDetailMap = SlotEventDetailMap & NodeEventDetailMap;

/**
 * Maps all events and their payload to an corresponding `CustomEvent` type.
 */
export type MappedCustomEvent<MAP> = { [KEY in keyof MAP]: CustomEvent<MAP[KEY]> }

declare global {
    // strong typings for SystemEvents and SystemSlotEvents when using `window.addEventListener`
    interface WindowEventMap extends MappedCustomEvent<WindowEventDetailMap> {
    }

    // strong typings for SlotEvents and NodeEvents when using `htmlElement.addEventListener`
    interface HTMLElementEventMap extends MappedCustomEvent<HtmlElementEventDetailMap> {
    }
}
